import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { UploadCloud, Receipt as ReceiptIcon, FileImage, ArrowRight, Loader2, Zap, CheckCircle2, AlertTriangle, Mail, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import OnboardingWizard from "@/pages/OnboardingWizard";

function StatCard({ label, value, hint, testId, children }) {
    return (
        <div className="tk-card p-6" data-testid={testId}>
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#86868B]">{label}</p>
            <p className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">{value}</p>
            {hint && <p className="mt-2 text-sm text-[#86868B]">{hint}</p>}
            {children}
        </div>
    );
}

function QuotaCard({ stats }) {
    if (!stats) return <StatCard label="Ce mois-ci" value="—" testId="stat-month" />;
    const limit = stats.plan.monthly_limit;
    const used = stats.this_month;
    const unlimited = limit > 1000;
    const pct = unlimited ? 0 : Math.min(100, Math.round((used / Math.max(1, limit)) * 100));
    const overLimit = !unlimited && used >= limit;
    const nearLimit = !unlimited && !overLimit && used >= limit * 0.8;
    const barColor = overLimit ? "#FF3B30" : nearLimit ? "#FF9F0A" : "#34C759";
    return (
        <div className="tk-card p-6" data-testid="stat-month">
            <p className="text-xs font-bold uppercase tracking-[0.15em] text-[#86868B]">Ce mois-ci</p>
            <p className="mt-4 text-3xl sm:text-4xl font-semibold tracking-tight text-[#1D1D1F]">
                {used}<span className="text-[#86868B] font-normal">/{unlimited ? "∞" : limit}</span>
            </p>
            {!unlimited && (
                <div className="mt-3 h-2 w-full rounded-full bg-black/[0.06] overflow-hidden">
                    <div
                        className="h-full rounded-full transition-all"
                        style={{ width: `${pct}%`, backgroundColor: barColor }}
                        data-testid="quota-bar"
                    />
                </div>
            )}
            <p className="mt-2 text-sm text-[#86868B]" data-testid="quota-hint">
                {unlimited
                    ? "Justificatifs illimités"
                    : overLimit
                    ? `Limite ${limit} atteinte — passez à un plan supérieur`
                    : `${stats.remaining} justificatifs restants — limite ${limit}/mois`}
            </p>
            {(overLimit || nearLimit) && stats.plan.id === "free" && (
                <Link
                    to="/app/forfait"
                    className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#007AFF] hover:underline"
                    data-testid="quota-upgrade-cta"
                >
                    Augmenter mon abonnement <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            )}
        </div>
    );
}

function fmtMoney(v, cur = "EUR") {
    if (v == null) return "—";
    try {
        return new Intl.NumberFormat("fr-FR", { style: "currency", currency: cur }).format(v);
    } catch {
        return `${v} ${cur}`;
    }
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
    } catch {
        return iso;
    }
}

function ComplianceBar({ autoStats }) {
    if (!autoStats || !autoStats.transactions_total) return null;
    const total = autoStats.transactions_total;
    const justified = autoStats.justificatifs_found;
    const missing = autoStats.missing + autoStats.manual_review;
    const pct = Math.round((justified / total) * 100);
    const complete = pct >= 100;
    const barColor = complete ? "#34C759" : pct >= 60 ? "#007AFF" : "#FF9F0A";
    return (
        <div className="tk-rise tk-card p-6" data-testid="compliance-bar">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div
                        className="w-10 h-10 rounded-2xl flex items-center justify-center"
                        style={{ backgroundColor: `${barColor}1A` }}
                    >
                        <BadgeCheck className="w-5 h-5" style={{ color: barColor }} />
                    </div>
                    <div>
                        <p className="text-sm font-semibold text-[#1D1D1F]" data-testid="compliance-pct">
                            {pct}&nbsp;% de vos transactions sont justifiées
                        </p>
                        <p className="text-xs text-[#86868B]">
                            {complete
                                ? "Votre comptabilité est complète — bravo !"
                                : `${missing} transaction${missing > 1 ? "s" : ""} sans justificatif sur ${total}`}
                        </p>
                    </div>
                </div>
                {!complete && (
                    <Link
                        to="/app/transactions"
                        className="tk-btn tk-btn-secondary !py-2 !px-4 text-sm"
                        data-testid="compliance-cta"
                    >
                        Importer les justificatifs manquants <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                )}
            </div>
            <div className="mt-4 h-2.5 w-full rounded-full bg-black/[0.06] overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, backgroundColor: barColor }}
                    data-testid="compliance-progress"
                />
            </div>
        </div>
    );
}

export default function Dashboard() {
    const navigate = useNavigate();
    const inputRef = useRef(null);
    const [stats, setStats] = useState(null);
    const [autoStats, setAutoStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [dragOver, setDragOver] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const [s, r, a] = await Promise.all([
                api.get("/receipts/stats"),
                // Recent activity must reflect BANK CSV transactions only,
                // not email-extracted receipts (which would double-list every
                // matched payment and surface Amazon line-item totals).
                api.get("/transactions", { params: { limit: 5 } }),
                api.get("/dashboard/automation"),
            ]);
            setStats(s.data);
            setRecent(r.data);
            setAutoStats(a.data);
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        refresh();
    }, [refresh]);

    const handleFiles = async (files) => {
        if (!files || files.length === 0) return;
        const file = files[0];
        const isPdf = file.type === "application/pdf" || /\.pdf$/i.test(file.name);
        if (!file.type.startsWith("image/") && !isPdf) {
            toast.error("Format non supporté. Utilisez JPEG, PNG, WEBP ou PDF.");
            return;
        }
        setUploading(true);
        const tid = toast.loading(isPdf ? "Analyse du PDF en cours…" : "Analyse du ticket en cours…");
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/receipts/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Ticket numérisé !", { id: tid });
            await refresh();
            navigate(`/app/receipt/${data.receipt_id}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Échec de l'upload", { id: tid });
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="max-w-7xl mx-auto px-6 sm:px-8 space-y-10">
            <div className="tk-rise">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Bonjour</p>
                <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[#1D1D1F]">
                    Déposez votre prochain ticket.
                </h1>
            </div>

            <ComplianceBar autoStats={autoStats} />

            <OnboardingWizard />

            {/* Upload zone */}
            <div
                onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                    e.preventDefault();
                    setDragOver(false);
                    handleFiles(e.dataTransfer.files);
                }}
                onClick={() => !uploading && inputRef.current?.click()}
                data-testid="upload-zone"
                className={`tk-rise tk-rise-1 rounded-3xl p-12 sm:p-16 text-center transition-all cursor-pointer flex flex-col items-center gap-4 border-2 border-dashed ${
                    dragOver ? "border-[#007AFF] bg-[#007AFF]/5" : "border-[#AEAEB2] bg-white/50 hover:bg-white"
                }`}
            >
                <input
                    ref={inputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,application/pdf,.pdf"
                    className="hidden"
                    onChange={(e) => handleFiles(e.target.files)}
                    data-testid="upload-input"
                />
                <div className="w-16 h-16 rounded-2xl bg-[#F5F5F7] flex items-center justify-center">
                    {uploading ? (
                        <Loader2 className="w-7 h-7 text-[#1D1D1F] animate-spin" />
                    ) : (
                        <UploadCloud className="w-8 h-8 text-[#1D1D1F]" />
                    )}
                </div>
                <div>
                    <p className="text-xl font-semibold text-[#1D1D1F]">
                        {uploading ? "Analyse en cours…" : "Glissez votre ticket ici"}
                    </p>
                    <p className="mt-1 text-sm text-[#86868B]">ou cliquez pour sélectionner — JPEG, PNG, WEBP ou PDF</p>
                </div>
                {!uploading && (
                    <button className="tk-btn tk-btn-primary mt-2" data-testid="upload-btn-cta">
                        Choisir un fichier
                    </button>
                )}
            </div>

            {/* Automation block */}
            {autoStats && autoStats.transactions_total > 0 && (
                <section className="tk-rise tk-rise-2" data-testid="dashboard-automation">
                    <div className="flex items-end justify-between mb-6">
                        <div>
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#86868B]">Automatisation</p>
                            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[#1D1D1F]">
                                Récupération automatique des justificatifs
                            </h2>
                        </div>
                        <Link to="/app/transactions" className="text-[#007AFF] text-sm font-medium inline-flex items-center gap-1">
                            Voir les transactions <ArrowRight className="w-4 h-4" />
                        </Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="tk-card p-5" data-testid="auto-rate">
                            <div className="flex items-center gap-2 mb-3">
                                <Zap className="w-4 h-4 text-[#007AFF]" />
                                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">Taux d'auto</p>
                            </div>
                            <p className="text-3xl font-semibold tracking-tight text-[#1D1D1F]">{autoStats.automation_rate}%</p>
                            <div className="mt-3 h-1.5 bg-[#F5F5F7] rounded-full overflow-hidden">
                                <div className="h-full bg-[#007AFF] rounded-full" style={{ width: `${autoStats.automation_rate}%` }} />
                            </div>
                        </div>
                        <div className="tk-card p-5" data-testid="auto-found">
                            <div className="flex items-center gap-2 mb-3">
                                <CheckCircle2 className="w-4 h-4 text-[#34C759]" />
                                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">Justifiées</p>
                            </div>
                            <p className="text-3xl font-semibold tracking-tight text-[#1D1D1F]">{autoStats.justificatifs_found}</p>
                            <p className="text-sm text-[#86868B] mt-2">sur {autoStats.transactions_total} transactions</p>
                        </div>
                        <div className="tk-card p-5" data-testid="auto-missing">
                            <div className="flex items-center gap-2 mb-3">
                                <AlertTriangle className="w-4 h-4 text-[#FF3B30]" />
                                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">Manquantes</p>
                            </div>
                            <p className="text-3xl font-semibold tracking-tight text-[#1D1D1F]">{autoStats.missing}</p>
                            <p className="text-sm text-[#86868B] mt-2">à compléter</p>
                        </div>
                        <div className="tk-card p-5" data-testid="auto-email">
                            <div className="flex items-center gap-2 mb-3">
                                <Mail className="w-4 h-4 text-[#1D1D1F]" />
                                <p className="text-xs font-bold uppercase tracking-wider text-[#86868B]">Email</p>
                            </div>
                            <p className="text-3xl font-semibold tracking-tight text-[#1D1D1F]">{autoStats.receipts_imported_by_email}</p>
                            <p className="text-sm text-[#86868B] mt-2">
                                {autoStats.last_sync?.last_sync ? "Sync OK" : "reçus importés"}
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                <div className="tk-rise tk-rise-2">
                    <QuotaCard stats={stats} />
                </div>
                <div className="tk-rise tk-rise-3">
                    <StatCard
                        label="Total dépensé"
                        value={stats ? fmtMoney(stats.spent_this_month, "EUR") : "—"}
                        hint="Toutes vos transactions importées"
                        testId="stat-spent"
                    />
                </div>
                <div className="tk-rise tk-rise-4">
                    <StatCard
                        label="Plan actif"
                        value={stats?.plan?.name || "—"}
                        hint={<Link to="/app/forfait" className="text-[#007AFF] hover:underline">Gérer mon abonnement</Link>}
                        testId="stat-plan"
                    />
                </div>
            </div>

            {/* Recent */}
            <div className="tk-rise tk-rise-5">
                <div className="flex items-end justify-between mb-6">
                    <h2 className="text-2xl font-semibold tracking-tight text-[#1D1D1F]">Activité récente</h2>
                    <Link to="/app/transactions" className="text-[#007AFF] text-sm font-medium inline-flex items-center gap-1" data-testid="recent-view-all">
                        Tout voir <ArrowRight className="w-4 h-4" />
                    </Link>
                </div>
                {recent.length === 0 ? (
                    <div className="tk-card p-12 text-center">
                        <FileImage className="w-10 h-10 text-[#AEAEB2] mx-auto mb-4" />
                        <p className="text-[#86868B]">Aucune transaction pour l'instant. Importez votre relevé CSV pour commencer.</p>
                    </div>
                ) : (
                    <div className="tk-card divide-y divide-black/5 overflow-hidden">
                        {recent.map((t) => (
                            <Link
                                to={t.matched_receipt ? `/app/receipt/${t.matched_receipt.receipt_id}` : `/app/transactions`}
                                key={t.transaction_id}
                                className="flex items-center gap-4 p-5 hover:bg-[#F5F5F7] transition"
                                data-testid={`recent-row-${t.transaction_id}`}
                            >
                                <div className="w-11 h-11 rounded-xl bg-[#F5F5F7] flex items-center justify-center">
                                    <ReceiptIcon className="w-5 h-5 text-[#1D1D1F]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <p className="font-medium text-[#1D1D1F] truncate">{t.merchant_raw || t.merchant_normalized || "Transaction"}</p>
                                    <p className="text-sm text-[#86868B]">
                                        {fmtDate(t.transaction_date)}
                                        {t.card_label ? ` · ${t.card_label}` : ""}
                                        {t.card_last4 ? ` ••${t.card_last4}` : ""}
                                    </p>
                                </div>
                                <div className="text-right">
                                    <p className="font-semibold text-[#1D1D1F]">{fmtMoney(t.amount, t.currency)}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
